// Centralized PWA compatibility checks used by CompatibilityCheckPage and WebAppStatus

const isSecureContext = () => (
    window.location.protocol === 'https:' || ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
);

async function checkServiceWorkerCompat(restrictions) {
    const isSupported = 'serviceWorker' in navigator;
    restrictions.serviceWorker.isSupported = isSupported;
    if (!isSupported) return false;

    if (process.env.NODE_ENV === 'development') {
        restrictions.serviceWorker.isBlocked = true;
        restrictions.serviceWorker.blockReason = 'Service Workers are disabled in development mode';
        return false;
    }

    if (!isSecureContext()) {
        restrictions.serviceWorker.isBlocked = true;
        restrictions.serviceWorker.blockReason = 'Service Workers require HTTPS';
        return false;
    }

    return true;
}

async function checkCacheAPICompat(restrictions) {
    const isSupported = 'caches' in window;
    restrictions.cacheAPI.isSupported = isSupported;
    if (!isSupported) return false;
    try {
        const testCache = await caches.open('test-cache');
        await testCache.put('/test', new Response('test'));
        await caches.delete('test-cache');
        return true;
    } catch (error) {
        restrictions.cacheAPI.isBlocked = true;
        restrictions.cacheAPI.blockReason = error?.message || 'Storage access is restricted (possibly in private/incognito mode)';
        return false;
    }
}

function checkInstallCompat(restrictions) {
    const isIosSafari = /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
    const supportsPrompt = 'onbeforeinstallprompt' in window;
    const isStandalone = (!!window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (/** @type {*} */(navigator)).standalone === true;
    restrictions.installation.isSupported = isIosSafari || supportsPrompt || isStandalone;
    if (!restrictions.installation.isSupported) {
        restrictions.installation.isBlocked = true;
        restrictions.installation.blockReason = isIosSafari
            ? 'Use "Add to Home Screen" from the Share menu on iOS'
            : 'Browser does not expose install prompt; use browser\'s Install menu if available';
        return false;
    }
    return true;
}

export async function checkPwaCompatibility() {
    const restrictions = {
        serviceWorker: { isBlocked: false, isSupported: false, blockReason: null },
        cacheAPI: { isBlocked: false, isSupported: false, blockReason: null },
        installation: { isBlocked: false, isSupported: false, blockReason: null }
    };

    const [swOk, cacheOk] = await Promise.all([
        checkServiceWorkerCompat(restrictions),
        checkCacheAPICompat(restrictions)
    ]);
    const installOk = checkInstallCompat(restrictions);

    return {
        restrictions,
        hasServiceWorker: !!swOk,
        hasCacheAPI: !!cacheOk,
        canBeInstalled: !!installOk,
        isSecure: isSecureContext()
    };
}

export { isSecureContext };

